import React from 'react'
import { Controller, useWatch } from 'react-hook-form'

import { FormTextInput } from 'Components/Inputs/FormTextInput'
import { FormAmountInput } from 'Components/Inputs/FormAmountInput'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ControlledSelect } from 'Components/Inputs/ControlledSelect'
import { ControlledSwitch } from 'Components/Inputs/Switch/ControlledSwitch'
import { BenefitProgramStartDateSelector } from './BenefitProgramStartDateSelector'
import { BenefitProgramEndDateSelector } from './BenefitProgramEndDateSelector'
import {
  OneTimeDuration,
  useBenefitProgramForm
} from 'Pages/JOONAdmin/EmployerDetailsPage/Views/BenefitPrograms/CreateBenefitProgramModal/useBenefitProgramForm'
import { useCountries } from 'Hooks/useCountries'
import { useGetBenefitProgramCategoriesQuery } from 'Hooks/api/JOONAdmin/useGetBenefitProgramCategoriesQuery'
import { CategoryMultiselect } from 'Components/Chip/CategoryMultiselect'
import { normalizeTypedAmount } from 'Utils/helpers/currency'
import { ButtonContainer, FlexRow, spacingLG } from 'Utils/styles/spacing'
import { EmployerBenefitProgram } from 'Utils/types/benefitProgram'
import { CountryMultiselect } from 'Components/Chip/CountryMultiselect'

type BenefitProgramFormProps = {
  selectedProgram?: EmployerBenefitProgram
  closeModal: () => void
}

export const BenefitProgramForm = ({ selectedProgram, closeModal }: BenefitProgramFormProps) => {
  const {
    register,
    control,
    handleSubmit,
    currencySymbol,
    errors,
    programCadence,
    setProgramCadence,
    rollover,
    setRollover,
    rolloverOptions,
    handleSubmission,
    handleError,
    policyPeriodOptions,
    benefitProgramRegisterOptions,
    categoryTaxableSettings,
    handleTaxableChange,
    isAnnual,
    isOneTime,
    annualResetMonth,
    setAnnualResetMonth,
    annualResetMonthOptions,
    oneTimeDuration,
    setOneTimeDuration,
    oneTimeDurationOptions,
    dateBasis,
    endDateBasis,
    scheduledStartDate
  } = useBenefitProgramForm(closeModal, selectedProgram)

  const { showCountries, countryOptions } = useCountries()

  const { data: programCategories } = useGetBenefitProgramCategoriesQuery()

  const ongoingPurchasesEnabled = useWatch({ control, name: 'ongoingPurchases' })

  return (
    <form onSubmit={handleSubmit(handleSubmission, handleError)}>
      <ModalBody>
        <FlexRow gap={spacingLG}>
          <FormTextInput
            fieldName="name"
            errorPresent={!!errors?.name?.message}
            labelId="createBenefitProgram.programName.label"
            register={register}
            registerOptions={benefitProgramRegisterOptions.name}
          />
          {!selectedProgram && (
            <FormAmountInput
              fieldName="allowance"
              currencySymbol={currencySymbol}
              errorPresent={!!errors?.allowance?.message}
              labelId="createBenefitProgram.allowance.label"
              disabled={!!selectedProgram}
              register={register}
              registerOptions={benefitProgramRegisterOptions.allowance}
            />
          )}
        </FlexRow>

        <FlexRow gap={spacingLG}>
          <FormTextInput
            fieldName="benefitProgramLink"
            errorPresent={!!errors?.benefitProgramLink?.message}
            labelId="createBenefitProgram.benefitProgramLink.label"
            register={register}
            registerOptions={benefitProgramRegisterOptions.benefitProgramLink}
          />
          {!selectedProgram && (
            <ControlledSelect
              label="Policy Period Duration"
              options={policyPeriodOptions}
              disabled={!!selectedProgram}
              defaultValue={programCadence}
              setControlValue={setProgramCadence}
            />
          )}
        </FlexRow>

        {!selectedProgram && isAnnual && (
          <FlexRow gap={spacingLG}>
            <ControlledSelect
              label="Annual Reset Month"
              options={annualResetMonthOptions}
              defaultValue={annualResetMonth}
              setControlValue={setAnnualResetMonth}
            />
          </FlexRow>
        )}

        {!selectedProgram && isOneTime && (
          <FlexRow gap={spacingLG}>
            <ControlledSelect
              label="Benefit Duration"
              options={oneTimeDurationOptions}
              defaultValue={oneTimeDuration}
              setControlValue={setOneTimeDuration}
            />
            {oneTimeDuration === OneTimeDuration.Days && (
              <FormTextInput
                fieldName="eligibilityDurationDays"
                labelId="createBenefitProgram.eligibilityDurationDays.label"
                register={register}
                errorPresent={!!errors?.eligibilityDurationDays?.message}
                registerOptions={benefitProgramRegisterOptions.eligibilityDurationDays}
                normalizeValue={(event) => event.target.value.replace(/\D/g, '')}
              />
            )}
          </FlexRow>
        )}

        {!selectedProgram && (
          <FlexRow gap={spacingLG}>
            <Controller
              control={control}
              name="scheduledStartDate"
              rules={benefitProgramRegisterOptions.scheduledStartDate}
              render={({ field: { onChange } }) => (
                <BenefitProgramStartDateSelector
                  key={dateBasis}
                  policyDuration={programCadence}
                  annualResetMonth={annualResetMonth ? Number(annualResetMonth) : undefined}
                  includeCurrentPeriod
                  onChange={onChange}
                />
              )}
            />

            {!isOneTime && (
              <Controller
                control={control}
                name="scheduledEndDate"
                render={({ field: { onChange } }) => (
                  <BenefitProgramEndDateSelector
                    key={endDateBasis}
                    policyDuration={programCadence}
                    annualResetMonth={annualResetMonth ? Number(annualResetMonth) : undefined}
                    allowIndefinite
                    after={scheduledStartDate}
                    onChange={onChange}
                  />
                )}
              />
            )}
          </FlexRow>
        )}

        <FlexRow gap={spacingLG}>
          {showCountries && (
            <CountryMultiselect
              options={countryOptions}
              control={control}
              name="countries"
              label="Program Countries"
              defaultSelected={selectedProgram?.countries ?? []}
            />
          )}
          {programCategories && (
            <CategoryMultiselect
              options={programCategories}
              control={control}
              name="selectedCategories"
              defaultSelected={selectedProgram?.categories.map((c) => c.id) ?? []}
              defaultTaxableSettings={categoryTaxableSettings}
              onTaxableChange={handleTaxableChange}
            />
          )}
        </FlexRow>

        {!selectedProgram && !isOneTime && (
          <FlexRow gap={spacingLG}>
            {!isAnnual && (
              <ControlledSelect
                label="Rollover"
                options={rolloverOptions}
                disabled={!!selectedProgram}
                defaultValue={rollover}
                setControlValue={setRollover}
              />
            )}
            <ControlledSwitch
              label="Ongoing Purchases"
              control={control}
              name="ongoingPurchases"
              disabled={!!selectedProgram || isAnnual}
            />
          </FlexRow>
        )}

        {!selectedProgram && !isOneTime && ongoingPurchasesEnabled && (
          <FlexRow gap={spacingLG}>
            <FormTextInput
              fieldName="ongoingPurchaseThreshold"
              labelId="createBenefitProgram.ongoingPurchaseThreshold.label"
              register={register}
              disabled={!!selectedProgram}
              errorPresent={!!errors?.ongoingPurchaseThreshold?.message}
              registerOptions={benefitProgramRegisterOptions.ongoingPurchaseThreshold}
              normalizeValue={(event) => normalizeTypedAmount(event.target.value)}
            />
          </FlexRow>
        )}

        <ButtonContainer>
          <StyledButton primary id="save.label" isLoading={false} />
        </ButtonContainer>
      </ModalBody>
    </form>
  )
}
